/**
 * The patient's card, as one chip in the balance header (gdn-3000).
 *
 * Replaces the full Payment Methods section under the balance-table gate.
 * Most patients have exactly one method, so a titled section with a
 * bordered card per row spent half a column to say one line. The chip says
 * the same line and opens the same list to edit it.
 *
 * Reuses the block's hook, list and add-modal — only the chrome differs.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { usePatientPaymentMethods } from '../store/hooks';

import { AddPatientPaymentMethodModal } from './AddPatientPaymentMethodModal';
import { getCardBrandDisplay } from './PatientPaymentMethodCard';
import { PatientPaymentMethodList } from './PatientPaymentMethodList';

export interface PatientPaymentMethodChipProps {
  patientId: string;
}

export function PatientPaymentMethodChip({ patientId }: PatientPaymentMethodChipProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { patientPaymentMethod } = useEventTracking();
  const [isListOpen, setIsListOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [retryingPatientId, setRetryingPatientId] = useState<string | null>(null);
  const isRetrying = retryingPatientId === patientId;
  const activeRetry = useRef<symbol | null>(null);

  // A completed lookup belongs to the view that initiated it. Invalidate
  // its notification when that patient changes or the component leaves.
  useEffect(
    () => () => {
      activeRetry.current = null;
    },
    [patientId],
  );

  const {
    paymentMethods,
    isStripeEnabled,
    isLoading,
    isSettingDefault,
    isDeleting,
    error,
    deleteMethod,
    setDefault,
    refetch,
  } = usePatientPaymentMethods(patientId);

  const handleRetry = useCallback(async () => {
    if (isRetrying) return;
    const retryAttempt = Symbol();
    activeRetry.current = retryAttempt;
    setRetryingPatientId(patientId);
    patientPaymentMethod?.retryClicked(patientId);
    let retryFailed = false;
    try {
      // RTK Query resolves HTTP failures as an error result; only a
      // rejected refetch promise reaches the defensive catch below.
      const result = await refetch();
      retryFailed = Boolean(result.error);
    } catch {
      retryFailed = true;
    } finally {
      setRetryingPatientId(current => (current === patientId ? null : current));
    }
    if (retryFailed && activeRetry.current === retryAttempt) {
      enqueueSnackbar('Could not reload payment methods. Please try again.', { variant: 'error' });
    }
  }, [isRetrying, patientId, patientPaymentMethod, refetch, enqueueSnackbar]);

  const handleOpen = useCallback(() => {
    setIsListOpen(true);
    patientPaymentMethod?.panelExpanded(patientId, {
      payment_methods_count: paymentMethods.length,
    });
  }, [patientId, patientPaymentMethod, paymentMethods.length]);

  const handleAddClick = useCallback(() => {
    patientPaymentMethod?.addClicked(patientId);
    setIsAddOpen(true);
  }, [patientId, patientPaymentMethod]);

  const handleAddSuccess = useCallback(() => {
    patientPaymentMethod?.added(patientId);
    enqueueSnackbar('Payment method added successfully', { variant: 'success' });
  }, [patientId, patientPaymentMethod, enqueueSnackbar]);

  const handleDelete = useCallback(
    async (paymentMethodId: string) => {
      try {
        await deleteMethod(paymentMethodId);
        patientPaymentMethod?.deleted(patientId, { payment_method_id: paymentMethodId });
        enqueueSnackbar('Payment method removed', { variant: 'success' });
      } catch (err) {
        patientPaymentMethod?.deleteFailed(patientId, {
          payment_method_id: paymentMethodId,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
        enqueueSnackbar('Failed to remove payment method', { variant: 'error' });
      }
    },
    [deleteMethod, patientId, patientPaymentMethod, enqueueSnackbar],
  );

  const handleSetDefault = useCallback(
    async (paymentMethodId: string) => {
      try {
        await setDefault(paymentMethodId);
        patientPaymentMethod?.setDefault(patientId, {
          payment_method_id: paymentMethodId,
        });
        enqueueSnackbar('Default payment method updated', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to update default payment method', { variant: 'error' });
      }
    },
    [setDefault, patientId, patientPaymentMethod, enqueueSnackbar],
  );

  // Same rule as the block it replaces: without Stripe Connect there is no
  // card to have, so the header says nothing rather than saying "none".
  if (!isStripeEnabled) return null;

  if (isLoading && !isRetrying) {
    return (
      <Chip
        icon={<CircularProgress size={11} sx={{ ml: 1 }} />}
        label="Card"
        size="small"
        sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600, borderRadius: '11px' }}
      />
    );
  }

  const primary = paymentMethods.find(method => method.is_default) ?? paymentMethods[0];
  const statusUnavailable = Boolean(error) || isRetrying;
  const label = statusUnavailable
    ? 'Card status unavailable'
    : primary
      ? primary.type === 'us_bank_account'
        ? `Bank •••• ${primary.card_last4 ?? ''}`.trim()
        : `${getCardBrandDisplay(primary.card_brand)} •••• ${primary.card_last4 ?? ''}`.trim()
      : 'No card on file';

  return (
    <>
      <Chip
        icon={
          statusUnavailable ? (
            <ErrorOutlinedIcon sx={{ fontSize: 15 }} />
          ) : primary ? (
            <CreditCardIcon sx={{ fontSize: 15 }} />
          ) : (
            <CreditCardOffIcon sx={{ fontSize: 15 }} />
          )
        }
        label={label}
        size="small"
        // A failed read cannot establish whether a card is on file.
        color={statusUnavailable ? 'error' : primary ? 'default' : 'warning'}
        variant={statusUnavailable || primary ? 'outlined' : 'filled'}
        onClick={handleOpen}
        aria-label={
          statusUnavailable
            ? 'Card status unavailable. Retry.'
            : primary
              ? `Payment method: ${label}. Manage.`
              : 'No card on file. Add one.'
        }
        sx={{
          height: 22,
          fontSize: '0.6875rem',
          fontWeight: 600,
          borderRadius: '11px',
          cursor: 'pointer',
        }}
      />

      <Dialog open={isListOpen} onClose={() => setIsListOpen(false)} maxWidth="xs" fullWidth>
        {/* Add lives in the title row, as it did in the section header this
            dialog replaces. The list only offers an add button in its empty
            state, so without this a patient with one card on file has no way
            to get a second. */}
        <DialogTitle
          sx={{
            fontSize: '1rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Payment methods
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{ textTransform: 'none', borderRadius: '0.5rem' }}
          >
            Add
          </Button>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {statusUnavailable ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" disabled={isRetrying} onClick={handleRetry}>
                  {isRetrying ? 'Retrying…' : 'Retry'}
                </Button>
              }
            >
              Failed to load payment methods. Please try again.
            </Alert>
          ) : (
            <PatientPaymentMethodList
              paymentMethods={paymentMethods}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              onAddClick={handleAddClick}
              isSettingDefault={isSettingDefault}
              isDeleting={isDeleting}
            />
          )}
        </DialogContent>
      </Dialog>

      <AddPatientPaymentMethodModal
        open={isAddOpen}
        patientId={patientId}
        onClose={() => setIsAddOpen(false)}
        onSuccess={handleAddSuccess}
      />
    </>
  );
}

export default PatientPaymentMethodChip;
